import { useLazyQuery } from "@apollo/client";
import { isEmpty } from "lodash";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_AI_RESPONSE } from "../../../../graphql/query";
import {
  getEmailAiDraftContent,
  getEmailAiResponseHistoryId,
  getTextAiDraftContent,
  getTextAiResponseHistoryId,
  setEmailAiDraftContent,
  setEmailAiResponseHistoryId,
  setTextAiDraftContent,
  setTextAiResponseHistoryId,
} from "../../../../redux/slices/aiSlice";
import { AI_ACTION, AI_CHANNEL, AI_REQUEST_MODE } from "./constants";

// Which slice of aiData a channel reads and writes. Channels are kept apart so
// generating an email doesn't disturb a text draft, and vice versa.
const CHANNEL_STATE = {
  [AI_CHANNEL.TEXT]: {
    getHistoryId: getTextAiResponseHistoryId,
    getDraftContent: getTextAiDraftContent,
    setHistoryId: setTextAiResponseHistoryId,
    setDraftContent: setTextAiDraftContent,
  },
  [AI_CHANNEL.EMAIL]: {
    getHistoryId: getEmailAiResponseHistoryId,
    getDraftContent: getEmailAiDraftContent,
    setHistoryId: setEmailAiResponseHistoryId,
    setDraftContent: setEmailAiDraftContent,
  },
};

/**
 * Runs a getAiResponse call and puts the result into a composer.
 *
 * Owns everything that is the same whoever asks (EAI-1006): reading the draft
 * before it is destroyed, sending it as `existingMessage` with the right
 * `mode`, recording the before/after so the caller can offer Undo, restoring
 * the draft when nothing comes back, and tracking which action is in flight.
 *
 * Everything channel-specific — how content is read, cleared and written, and
 * which field of the response carries it — comes in via `adapter`; see
 * draftAdapters.js. That is what lets the Text composer, the review reply box
 * and the rich-text email composer share this.
 *
 * @param {{
 *   channel: string,                                 // an AI_CHANNEL
 *   adapter: object,                                 // see draftAdapters.js
 *   buildResponsePayload: (promptData: object) => object,
 *   setLoader?: Function,
 *   onDraftReplaced?: (v: {
 *     source: string, sourceSubject?: string, sourceHistoryId: number|null,
 *     result: string, resultSubject?: string, resultHistoryId: number|null,
 *   }) => void,
 * }} config
 * @returns {{ runAiDraft: Function, pendingAction: string|null }}
 */
export default function useAiDraftWriter({
  channel = AI_CHANNEL.TEXT,
  adapter,
  buildResponsePayload,
  setLoader,
  onDraftReplaced,
}) {
  const dispatch = useDispatch();
  const channelState = CHANNEL_STATE[channel] || CHANNEL_STATE[AI_CHANNEL.TEXT];

  // What is in the composer right now, as far as this channel is concerned.
  // Read before each call so a replaced draft can be reported with the response
  // id it was actually generated under.
  const currentHistoryId = useSelector(channelState.getHistoryId);
  const currentDraftContent = useSelector(channelState.getDraftContent);

  // Which AI_ACTION is in flight (null when idle), so a caller rendering two
  // actions side by side can spin only the one that was clicked.
  const [pendingAction, setPendingAction] = useState(null);

  const [getAiResponse] = useLazyQuery(GET_AI_RESPONSE, { nextFetchPolicy: "network-only" });

  /**
   * @param {{
   *   action?: string,         // an AI_ACTION; REFINE sends the draft to be reworked
   *   promptData?: object,     // { quickTips, tones, additionalNote }
   *   onSettled?: Function,    // e.g. close the panel this was fired from
   * }} args
   */
  const runAiDraft = async ({ action = AI_ACTION.GENERATE, promptData = {}, onSettled } = {}) => {
    // Read the draft BEFORE the composer is cleared — for a refine it is the
    // input, and either way it is the version Undo steps back to.
    const sourceText = adapter.readDraft();
    // Two readings of the same draft. `sourceText` is plain text and goes to the
    // API; `sourceSnapshot` keeps whatever form the composer needs to restore it
    // exactly — HTML for the rich email editor. Storing the plain text as the
    // undoable version would flatten the user's formatting and line breaks.
    const sourceSnapshot = adapter.readSnapshot ? adapter.readSnapshot() : sourceText;
    // Subject only exists on channels that have one (email). Captured alongside
    // the body so Undo can put the pair back together.
    const sourceSubject = adapter.readSubject?.();
    const isRefine = action === AI_ACTION.REFINE && sourceText.trim().length > 0;

    // Was the text we're about to replace Otto's work or the user's own? Only
    // Otto's can be rated, and only against the response id that produced it —
    // so the id travels with the version rather than being looked up later,
    // by which time redux holds the NEW response's id.
    // Compared against the snapshot, not the plain text: setDraftContent stores
    // what was written into the composer, which is the same form readSnapshot
    // returns. Comparing plain text to stored HTML would never match on email.
    const sourceHistoryId =
      currentDraftContent && currentDraftContent.trim() === sourceSnapshot.trim()
        ? currentHistoryId
        : null;

    setPendingAction(action);
    adapter.clearDraft();

    // A refine sends the draft as first-class input (`existingMessage` /
    // `existingSubject` on AiSuggestionUserInput) rather than smuggling it into
    // `additionalNote`, so the user's own instructions stay their own.
    //
    // These three fields go out together or not at all. The backend treats a
    // supplied draft as REFINE by default, so sending `existingMessage` without
    // `mode` would quietly turn a plain generate into a refine. Every other call
    // sends exactly what it sent before and lets the backend assume the default.
    const refineInput = isRefine
      ? {
          existingMessage: sourceText,
          existingSubject: sourceSubject || undefined,
          mode: AI_REQUEST_MODE.REFINE,
        }
      : {};

    const variables = buildResponsePayload({ ...promptData, ...refineInput });

    setLoader?.(true);
    let res;
    try {
      res = await getAiResponse({ variables, fetchPolicy: "network-only" });
    } finally {
      setPendingAction(null);
    }

    const payload = res?.data?.getAiResponse;
    const resultHistoryId = payload?.aiResponseHistoryId;
    dispatch(channelState.setHistoryId(resultHistoryId));

    const writeText = adapter.extractResult(payload);
    if (isEmpty(writeText)) {
      // Nothing came back — put the user's draft back rather than leaving them
      // with an empty composer they never cleared themselves.
      if (sourceSnapshot) {
        adapter.restoreDraft(sourceSnapshot);
        adapter.writeSubject?.(sourceSubject);
      }
      setLoader?.(false);
      onSettled?.();
      return;
    }

    // A refine that replaces text the user already had is undoable, so hand the
    // caller the before/after pair. Drafting into an empty composer destroys
    // nothing and needs no version.
    //
    // `isRefine` travels with it because a generate is reported too: Generate
    // means "I don't want this one, write me another", so it abandons the refine
    // chain rather than extending it. The version hook decides what that means —
    // see useDraftVersions.record.
    // Only channels that can apply a subject take one from the response —
    // otherwise a text draft would carry an email subject it has nowhere to put.
    const resultSubject = adapter.writeSubject ? payload?.subject : undefined;

    if (sourceText.trim()) {
      onDraftReplaced?.({
        isRefine,
        source: sourceSnapshot,
        sourceSubject,
        sourceHistoryId,
        result: writeText,
        resultSubject,
        resultHistoryId,
      });
    }

    onSettled?.();
    await adapter.writeDraft(writeText);
    adapter.writeSubject?.(resultSubject);
    // EAI-1762: remember exactly what Otto wrote, so the draft thumbs can hide
    // once the user edits it.
    dispatch(channelState.setDraftContent(writeText));
    setLoader?.(false);
  };

  return { runAiDraft, pendingAction };
}
